import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { config } from '../config/environment';

interface Playlist {
  id: string;
  name: string;
  description?: string;
  songIds: string[];
}

interface Session {
  sessionId: string;
  hostUserId: string;
  status: 'active' | 'paused' | 'ended';
  qrCode: string;
  qrCodeUrl: string;
  createdAt: string;
  currentSong?: string;
  currentSection?: string;
  audienceCount: number;
  settings: SessionSettings;
  playlists?: Playlist[];
}

interface SessionSettings {
  allowRequests: boolean;
  allowVoting: boolean;
  audienceCanSeeChords: boolean;
}

interface CreateSessionRequest {
  hostUserId: string;
  settings: SessionSettings;
  playlists?: Playlist[];
}

interface SessionStore {
  currentSession: Session | null;
  isLoading: boolean;
  error: string | null;
  websocket: WebSocket | null;
  createSession: (request: CreateSessionRequest) => Promise<Session>;
  joinSession: (sessionId: string) => Promise<void>;
  endSession: () => void;
  connectWebSocket: (sessionId: string, userRole: 'host' | 'audience') => void;
  disconnectWebSocket: () => void;
  sendSessionControl: (action: string, data?: any) => void;
  sendAudienceUpdate: (action: string, data?: any) => void;
  setCurrentSession: (session: Session | null) => void;
  setError: (error: string | null) => void;
}

const API_BASE_URL = config.apiUrl;
const WEBSOCKET_URL = config.websocketUrl;

export const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => ({
  currentSession: null,
  isLoading: false,
  error: null,
  websocket: null,

  createSession: async (request: CreateSessionRequest): Promise<Session> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE_URL}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`);
      }

      const session: Session = await response.json();
      set({ currentSession: session, isLoading: false });
      
      // Connect to WebSocket as host
      get().connectWebSocket(session.sessionId, 'host');
      
      return session;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  joinSession: async (sessionId: string): Promise<void> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to join session: ${response.statusText}`);
      }

      const joinResponse = await response.json();
      
      // Fetch full session details
      const sessionResponse = await fetch(`${API_BASE_URL}/sessions/${sessionId}`);
      if (sessionResponse.ok) {
        const session: Session = await sessionResponse.json();
        set({ currentSession: session, isLoading: false });
      } else {
        // If we can't get full details, at least set what we have
        set({ isLoading: false });
      }
      
      // Connect to WebSocket as audience
      get().connectWebSocket(sessionId, 'audience');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  endSession: () => {
    set({ currentSession: null });
    get().disconnectWebSocket();
    // Clear from localStorage as well
    localStorage.removeItem('commonchord-session');
  },

  connectWebSocket: (sessionId: string, userRole: 'host' | 'audience') => {
    // Disconnect existing WebSocket if any
    const existingWs = get().websocket;
    if (existingWs && existingWs.readyState === WebSocket.OPEN) {
      existingWs.close();
    }
    
    const websocketUrl = `${WEBSOCKET_URL}?sessionId=${sessionId}&userRole=${userRole}`;
    
    const websocket = new WebSocket(websocketUrl);
    
    websocket.onopen = () => {
      console.log('WebSocket connected to session:', sessionId);
      set({ websocket });
      
      // Send initial connection message
      const connectMessage = {
        action: 'connect',
        sessionId,
        userRole,
        timestamp: new Date().toISOString()
      };
      websocket.send(JSON.stringify(connectMessage));
    };

    websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('WebSocket message received:', message);
        
        // Handle different message types
        switch (message.type) {
          case 'SESSION_CONTROL':
            // Update current session state
            const { currentSession } = get();
            if (currentSession) {
              const updatedSession = { ...currentSession };
              if (message.data.songId) {
                updatedSession.currentSong = message.data.songId;
              }
              if (message.data.sectionId) {
                updatedSession.currentSection = message.data.sectionId;
              }
              set({ currentSession: updatedSession });
            }
            break;
          case 'AUDIENCE_UPDATE':
            // Update audience count
            const { currentSession: session } = get();
            if (session && message.data.audienceCount !== undefined) {
              set({ 
                currentSession: { 
                  ...session, 
                  audienceCount: message.data.audienceCount 
                } 
              });
            }
            break;
          case 'CONNECTION_ESTABLISHED':
            console.log('WebSocket connection established:', message.data);
            break;
          case 'ERROR':
            console.error('WebSocket server error:', message.data);
            set({ error: message.data.message || 'WebSocket error' });
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    websocket.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      set({ websocket: null });
      
      // Attempt to reconnect if it was an unexpected closure
      if (event.code !== 1000 && event.code !== 1001) {
        console.log('Attempting to reconnect in 3 seconds...');
        setTimeout(() => {
          const { currentSession } = get();
          if (currentSession && currentSession.sessionId === sessionId) {
            get().connectWebSocket(sessionId, userRole);
          }
        }, 3000);
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      set({ error: 'WebSocket connection failed' });
    };
  },

  disconnectWebSocket: () => {
    const { websocket } = get();
    if (websocket) {
      websocket.close();
      set({ websocket: null });
    }
  },

  sendSessionControl: (action: string, data?: any) => {
    const { websocket, currentSession } = get();
    if (websocket && websocket.readyState === WebSocket.OPEN && currentSession) {
      const message = {
        action: 'sendmessage',
        type: 'SESSION_CONTROL',
        sessionId: currentSession.sessionId,
        timestamp: new Date().toISOString(),
        data: {
          action,
          ...data
        }
      };
      websocket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not ready or no session:', { 
        readyState: websocket?.readyState, 
        hasSession: !!currentSession 
      });
    }
  },

  sendAudienceUpdate: (action: string, data?: any) => {
    const { websocket, currentSession } = get();
    if (websocket && websocket.readyState === WebSocket.OPEN && currentSession) {
      const message = {
        action: 'sendmessage',
        type: 'AUDIENCE_UPDATE',
        sessionId: currentSession.sessionId,
        timestamp: new Date().toISOString(),
        data: {
          action,
          ...data
        }
      };
      websocket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not ready or no session:', { 
        readyState: websocket?.readyState, 
        hasSession: !!currentSession 
      });
    }
  },

  setCurrentSession: (session: Session | null) => {
    set({ currentSession: session });
  },

  setError: (error: string | null) => {
    set({ error });
  },
}),
    {
      name: 'commonchord-session',
      partialize: (state) => ({
        currentSession: state.currentSession
      })
    }
  )
);